import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE = "https://parkhomesupport.co.uk";
const DEFAULT_IMG = "/images/og-default.jpg";

const BASE_ORG = {
  "@context": "https://schema.org",
  "@type": ["ProfessionalService", "LocalBusiness"],
  "@id": `${SITE}/#organization`,
  "name": "Park Home Support",
  "url": SITE,
  "logo": `${SITE}/images/phs-logo.png`,
  "image": `${SITE}/images/og-default.jpg`,
  "description":
    "Independent support for park home owners — quote review, inspections, insurance guidance and urgent repair advice before you commit to costly work.",
  "email": "advice@parkhomesupport.co.uk",
  "telephone": "+443335776047",
  "priceRange": "££",
  "areaServed": [
    { "@type": "Country", "name": "United Kingdom" },
    { "@type": "AdministrativeArea", "name": "England" },
    { "@type": "AdministrativeArea", "name": "Wales" },
    { "@type": "AdministrativeArea", "name": "Scotland" },
  ],
  "serviceType": [
    "Park Home Quote Review",
    "Park Home Inspection",
    "Park Home Insurance Guidance",
    "Park Home Urgent Repair Advice",
    "Park Home Membership Support",
  ],
  "address": { "@type": "PostalAddress", "addressCountry": "GB" },
  "sameAs": [],
  "contactPoint": [
    {
      "@type": "ContactPoint",
      "telephone": "+443335776047",
      "email": "advice@parkhomesupport.co.uk",
      "contactType": "customer service",
      "areaServed": "GB",
      "availableLanguage": ["English"],
    },
  ],
};

const PRETTY = {
  "how-it-works": "How It Works",
  "support-plan": "Support Plan",
  "quote-review": "Quote Review",
  "park-home-urgent-repairs": "Urgent Repairs",
  "park-home-repair-costs-uk": "Repair Costs UK",
  "park-home-roof-leak-advice": "Roof Leak Advice",
  "park-home-damp-problems": "Damp Problems",
  "park-home-insurance-claims-advice": "Insurance Claims Advice",
  "park-home-quote-too-high": "Is Your Quote Too High?",
  "park-home-assist-alternative": "Park Home Assist Alternative",
  "goldshield-alternative": "GoldShield Alternative",
  "park-home-insurance-alternatives": "Insurance Alternatives",
  "best-park-home-support-services": "Best Park Home Support Services",
  "park-home-maintenance-plan-alternative": "Maintenance Plan Alternative",
  "park-home-inspections-essex": "Inspections in Essex",
  "park-home-repairs-essex": "Repairs in Essex",
  "park-home-surveys-kent": "Surveys in Kent",
  "park-home-repairs-suffolk": "Repairs in Suffolk",
  "park-home-insurance-help-norfolk": "Insurance Help in Norfolk",
  "park-home-maintenance-cambridgeshire": "Maintenance in Cambridgeshire",
  "park-home-repairs-colchester": "Repairs in Colchester",
  "park-home-surveys-chelmsford": "Surveys in Chelmsford",
  "park-home-support-clacton": "Support in Clacton",
  "park-home-inspection-cost": "Inspection Cost",
  "park-home-underfloor-insulation-problem": "Underfloor Insulation",
  "park-home-inspection": "Park Home Inspection",
  "park-home-repairs": "Park Home Repairs",
  "park-home-maintenance": "Park Home Maintenance",
  "park-home-insurance-claim-support": "Insurance Claim Support",
  "park-home-quote-review-service": "Quote Review Service",
  "park-home-support-essex": "Essex",
  "park-home-support-kent": "Kent",
  "park-home-support-suffolk": "Suffolk",
  "park-home-support-norfolk": "Norfolk",
  "park-home-support-cambridgeshire": "Cambridgeshire",
  "who-is-responsible-for-park-home-repairs": "Repair Responsibility",
  "can-park-operator-refuse-repairs": "Operator Refused Repairs",
  "park-home-insurance-claim-rejected": "Claim Rejected?",
  "park-home-base-failure-signs": "Base Failure Signs",
  "understanding-mobile-homes-act": "Mobile Homes Act",
  "park-home-drainage-problems": "Drainage Problems",
  "leave-review": "Leave a Review",
  "request-support": "Request Support",
  "membership": "Membership",
  "operators": "Operators",
  "inspections": "Inspections",
  "insurance": "Insurance Guidance",
  "pay": "Pay an Invoice",
  "about": "About",
  "contact": "Contact",
  "privacy": "Privacy",
  "terms": "Terms",
};

function buildBreadcrumb(pathname) {
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length === 0) return null;
  const items = [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE },
    ...segs.map((seg, i) => ({
      "@type": "ListItem",
      position: i + 2,
      name: PRETTY[seg] || seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      item: `${SITE}/${segs.slice(0, i + 1).join("/")}`,
    })),
  ];
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  image = DEFAULT_IMG,
  type = "website",
  faq,
  noindex = false,
  breadcrumbs = true,
}) {
  const { pathname } = useLocation();
  const url = canonical || `${SITE}${pathname}`;
  const ogImage = image?.startsWith("http") ? image : `${SITE}${image}`;

  const schemas = [BASE_ORG];
  if (breadcrumbs) {
    const bc = buildBreadcrumb(pathname);
    if (bc) schemas.push(bc);
  }
  if (faq && faq.length) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Park Home Support" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:url" content={url} />
      <meta property="og:locale" content="en_GB" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
